import {FC, useState, useCallback, useEffect, useMemo} from 'react';
import {useParams} from 'wouter';

import {formatGroupId, FullGroup, groupName, GroupType} from '@shared/models/api_models';

import {showModal} from '@shared_web/components/core/modal';
import {Pagination} from '@shared_web/components/core/pagination';
import {SvgIcon} from '@shared_web/components/core/svg_icon';
import {Flex} from '@shared_web/components/core2/flex';
import {icon_building_2_fill} from '@shared_web/components/icons/icon_building_2_fill';
import {icon_team_fill} from '@shared_web/components/icons/icon_team_fill';

import {Line, TableWrapper} from '@mavismart_frontend/components/common_styles';
import {CustomGroupForm} from '@mavismart_frontend/components/custom_group_form';
import {InputSearch} from '@mavismart_frontend/components/input_search';
import {Loader} from '@mavismart_frontend/components/loader';
import {
  itemsCountLabel,
  itemsPerPageLabel,
  paginate,
  PAGINATION_MAX_LINES,
} from '@mavismart_frontend/components/pagination_utils';
import {StickySearchWrapper} from '@mavismart_frontend/components/sticky_search_wrapper';
import {Translate} from '@mavismart_frontend/components/translate';
import {icon_plus_line} from '@mavismart_frontend/icons/icon_plus_line';
import {
  CIRCLE_ICON_CONTAINER,
  MODAL_THEME,
  MODAL_TITLE_STYLE,
} from '@mavismart_frontend/lib/constants';
import {GenericCell} from '@mavismart_frontend/pages/cells/generic_cell';
import {getUserLocale, useAccount} from '@mavismart_frontend/stores/account_store';
import {useGroups} from '@mavismart_frontend/stores/groups_store';

export const GroupsList: FC = () => {
  const {groupId} = useParams<{groupId?: string}>();
  const account = useAccount();
  const rawGroups = useGroups();
  const groups = useMemo(() => {
    // For superadmins, we hide the virtual groups
    if (account?.hasSupportApp) {
      return rawGroups?.filter(group => !group.group.id.includes('__'));
    }
    return rawGroups;
  }, [rawGroups, account?.hasSupportApp]);
  const locale = getUserLocale(account);

  const [filteredGroups, setFilteredGroups] = useState<FullGroup[] | undefined>(groups);
  useEffect(() => setFilteredGroups(groups), [groups]);

  const getLabel = useCallback((fullGroup: FullGroup): string => {
    return `${groupName(fullGroup)} ${fullGroup.group.id}`;
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const paginatedGroups = paginate(filteredGroups, currentPage);

  const handleAddCustomGroup = useCallback(() => {
    showModal({
      title: (
        <MODAL_TITLE_STYLE>
          <Translate value="add_a_custom_group" />
        </MODAL_TITLE_STYLE>
      ),
      ...MODAL_THEME,
      children: <CustomGroupForm />,
    });
  }, []);

  if (paginatedGroups === undefined) {
    return (
      <Flex $align="center" $justify="center" $width="100%" $height="100%">
        <Loader />
      </Flex>
    );
  }

  return (
    <>
      <StickySearchWrapper>
        <Line>
          <InputSearch items={groups} setFilteredItems={setFilteredGroups} getLabel={getLabel} />
          {account?.canCreateCustomGroup && (
            <CIRCLE_ICON_CONTAINER onClick={handleAddCustomGroup}>
              <SvgIcon icon={icon_plus_line} size={15} />
            </CIRCLE_ICON_CONTAINER>
          )}
        </Line>
      </StickySearchWrapper>
      {paginatedGroups.length === 0 ? (
        <Translate value="no_group_found" />
      ) : (
        <TableWrapper>
          {paginatedGroups?.map(group => {
            const isHighlighted = groupId === group.group.id;
            const doorCountComponent = (
              <Translate
                value={
                  group.doorIds.length === 0
                    ? 'no_door'
                    : group.doorIds.length === 1
                      ? 'one_door'
                      : 'x_doors'
                }
                params={{count: group.doorIds.length}}
              />
            );
            return (
              <GenericCell
                key={group.group.id}
                id={group.group.id}
                title={groupName(group)}
                subtitle={
                  <Flex $gap="xs">
                    {group.group.type === GroupType.Groupe ? (
                      doorCountComponent
                    ) : (
                      <>
                        <span>{formatGroupId(group.group.id)}</span>
                        <span>&middot;</span>
                        {doorCountComponent}
                      </>
                    )}
                  </Flex>
                }
                icon={
                  <SvgIcon
                    size={24}
                    icon={
                      group.group.type === GroupType.Groupe ? icon_team_fill : icon_building_2_fill
                    }
                    color={'#00000020'}
                  />
                }
                to={`/groups/${group.group.id}`}
                highlighted={isHighlighted}
              />
            );
          })}
        </TableWrapper>
      )}
      {filteredGroups && (
        <Pagination
          totalItems={filteredGroups.length}
          limit={PAGINATION_MAX_LINES}
          onPageChange={setCurrentPage}
          itemsCountLabel={itemsCountLabel}
          itemsPerPageLabel={itemsPerPageLabel}
          locale={locale}
          singlePageBehavior="hide"
        />
      )}
    </>
  );
};

GroupsList.displayName = 'GroupsList';
